import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_a88f344e-8f3e-4749-8647-43dae95be213/artifacts/ivbj0d3u_Colorful%20Simple%20Minimalist%20Feel%20The%20Music%20Reddit%20Banner%20%283%29.png";

const ITEMS = [
  {
    title: "High-Alignment Matching",
    para1: "Matches only appear when there's strong alignment between both people.\nYou're seeing people who already fit what you're looking for.",
    para2: "Most platforms show you more people.\nHAEVN shows you fewer, better ones.",
    accent: "#008080",
    bg: "bg-gradient-to-br from-teal-50 to-cyan-50",
    border: "border-l-teal-500",
  },
  {
    title: "Passive Discovery",
    para1: "There's nothing to scroll through or sort.\nYou're not spending time searching for someone.",
    para2: "The system runs in the background and surfaces matches when they exist.",
    accent: "#1E2A4A",
    bg: "bg-gradient-to-br from-slate-50 to-indigo-50",
    border: "border-l-indigo-400",
  },
  {
    title: "Batched Matching",
    para1: "Matches are released together, once a week.\nBoth people see it at the same time.",
    para2: "No chasing. No guessing.\nJust a clear moment to decide if you want to connect.",
    accent: "#E29E0C",
    bg: "bg-gradient-to-br from-amber-50 to-orange-50",
    border: "border-l-amber-500",
  },
  {
    title: "Verified Identity",
    para1: "Profiles go through a verification process before entering the network.\nYou know who you're talking to.",
    para2: "This reduces noise and builds trust from the start.",
    accent: "#008080",
    bg: "bg-gradient-to-br from-emerald-50 to-teal-50",
    border: "border-l-emerald-500",
  },
  {
    title: "City-Based Activation",
    para1: "HAEVN launches one city at a time.\nEach network is built locally, then activated.",
    para2: "You're not dropped into an empty pool.\nWhen your city goes live, there are already real matches waiting.",
    accent: "#2563EB",
    bg: "bg-gradient-to-br from-blue-50 to-sky-50",
    border: "border-l-blue-500",
  },
  {
    title: "Membership Access",
    para1: "HAEVN is free to join and explore.\nYou can complete your survey and see your matches with no commitment.",
    para2: "Membership unlocks the ability to connect.\nYou join when you're ready, not just to browse.",
    accent: "#E29E0C",
    bg: "bg-gradient-to-br from-rose-50 to-pink-50",
    border: "border-l-rose-400",
  },
];

function DesktopExpandList() {
  const [active, setActive] = useState(0);
  const item = ITEMS[active];

  return (
    <div className="flex gap-0 min-h-[420px]" data-testid="anatomy-page-expand-list">
      <div className="w-[300px] flex-shrink-0 border-r border-gray-200">
        {ITEMS.map((it, i) => {
          const isActive = i === active;
          return (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-full text-left py-4 px-6 transition-all duration-250 border-l-[3px] ${
                isActive
                  ? `${it.border} bg-white`
                  : "border-l-transparent hover:bg-gray-50"
              }`}
            >
              <span
                className={`font-headline text-lg transition-colors duration-200 ${
                  isActive ? "text-haevn-navy" : "text-gray-400"
                }`}
              >
                {it.title}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex-1 flex items-center">
        <div
          key={active}
          className={`animate-fadeIn w-full h-full flex items-center rounded-r-xl ${item.bg}`}
        >
          <div className="pl-14 pr-10 py-10">
            <div
              className="w-8 h-1 mb-5 rounded-full"
              style={{ backgroundColor: item.accent }}
            />
            <h3 className="font-headline text-4xl lg:text-5xl text-haevn-navy mb-5 leading-[1.1]">
              {item.title}
            </h3>
            <div className="space-y-1 mb-4">
              {item.para1.split("\n").map((line, j) => (
                <p key={j} className="text-base text-haevn-navy font-body leading-relaxed">{line}</p>
              ))}
            </div>
            <div className="space-y-1">
              {item.para2.split("\n").map((line, j) => (
                <p key={j} className="text-base text-gray-500 font-body leading-relaxed">{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileAccordion() {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-2" data-testid="anatomy-mobile-accordion">
      {ITEMS.map((item, i) => {
        const isOpen = expanded === i;
        return (
          <div
            key={item.title}
            className={`rounded-lg overflow-hidden transition-all duration-300 ${isOpen ? item.bg : "bg-gray-50"}`}
          >
            <button
              onClick={() => setExpanded(isOpen ? null : i)}
              className="w-full flex items-center justify-between py-4 px-5 text-left"
            >
              <span className="font-headline text-xl text-haevn-navy">{item.title}</span>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-80 opacity-100 pb-5 px-5" : "max-h-0 opacity-0"}`}
            >
              <div className="space-y-1 mb-3">
                {item.para1.split("\n").map((line, j) => (
                  <p key={j} className="text-base text-haevn-navy font-body leading-relaxed">{line}</p>
                ))}
              </div>
              <div className="space-y-1">
                {item.para2.split("\n").map((line, j) => (
                  <p key={j} className="text-sm text-gray-500 font-body leading-relaxed">{line}</p>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AnatomyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="anatomy-page">
      <SEO
        title="Anatomy of a Matching Network"
        path="/anatomy"
        description="Explore the core components of HAEVN's matching network — from high-alignment matching to verification to weekly Match Monday releases."
      />
      <Navigation />

      {/* Header */}
      <section className="pt-24 pb-6 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-2">
              HAEVN Beneath The Surface
            </p>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl text-haevn-navy leading-tight mb-3">
              The Anatomy of a Matching Network
            </h1>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="text-lg md:text-xl text-gray-500 font-body max-w-2xl">
              How HAEVN replaces swiping with structured matching.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Intro */}
      <section className="pt-6 pb-2 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-lg text-gray-600 font-body leading-relaxed max-w-[780px]">
              HAEVN replaces active searching with structured matching.
              You define what you want once, and the system works in the background to surface people who actually fit.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Core System */}
      <section className="py-10 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="hidden md:block">
            <FadeIn>
              <DesktopExpandList />
            </FadeIn>
          </div>
          <div className="md:hidden">
            <FadeIn>
              <MobileAccordion />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Bottom — Synthesis */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy mb-4">
              This is what replaces swiping.
            </h2>
            <div className="max-w-[700px]">
              <p className="text-base text-gray-600 font-body leading-relaxed mb-1">
                HAEVN is designed as a system.
              </p>
              <p className="text-base text-gray-500 font-body leading-relaxed">
                You define what you want once, and the network filters for alignment.
                Matches are released intentionally, and connection happens when both people are ready.
              </p>
            </div>
          </FadeIn>

          {/* Bottom Links */}
          <FadeIn delay={100}>
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 text-sm font-subhead text-haevn-navy hover:text-haevn-teal transition-colors duration-200 group"
              >
                Explore How HAEVN Works
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/evolution"
                className="inline-flex items-center gap-2 text-sm font-subhead text-gray-400 hover:text-haevn-teal transition-colors duration-200 group"
              >
                Explore the Evolution of Digital Dating
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
